/**
 * DemoLayout — CaraLoom Controlled Demo Environment (iter 179).
 *
 * Shared shell for every /demo/preview* screen. Provides:
 *   - persistent CARALOOM DEMO watermark (bottom-right, tasteful)
 *   - a fictional-data pill under the header
 *   - a top navigation bar (Overview / Transitions / Placement / Network / Insights)
 *   - a floating Presenter Bar (only when presenterMode=true) — Reset,
 *     Focus toggle, and 1-click shortcuts to every demo stop
 *   - a "Start Eleanor Demo" button that takes the presenter directly
 *     into the primary story
 *
 * IP rules: no "Matching Engine", "Routing", "Scoring", "Decision Engine",
 * "Provider Ranking" surfaces. This nav is intentionally simple.
 */
import React from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, ListChecks, MapPin, Network, LineChart, Play,
  RefreshCw, Focus, Sparkles, Users, Printer,
} from 'lucide-react';
import { useDemo } from '../../contexts/DemoContext';
import { CaraLoomLogo } from '../../components/brand/CaraLoomLogo';
import { DEMO_META, ELEANOR_ARCHETYPES } from '../../data/demoData';

const NAV_ITEMS = [
  { key: 'overview',   label: 'Overview',    icon: LayoutDashboard, to: '/demo/preview' },
  { key: 'transitions',label: 'Transitions', icon: ListChecks,      to: '/demo/preview/transitions' },
  { key: 'placement',  label: 'Placement',   icon: MapPin,          to: '/demo/preview/patient/tx-1?view=placement' },
  { key: 'network',    label: 'Network',     icon: Network,         to: '/demo/preview/network' },
  { key: 'insights',   label: 'Insights',    icon: LineChart,       to: '/demo/preview/insights' },
];

export default function DemoLayout() {
  const {
    state, reset, presenterMode, setPresenterMode, focusMode, setFocusMode,
    access, personalizedOrg, archetype, setArchetype,
  } = useDemo();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hospital = state?.hospital || {};

  const isActive = (to) => {
    if (to === '/demo/preview') return pathname === '/demo/preview';
    return pathname.startsWith(to.split('?')[0]);
  };

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text" data-testid="demo-layout">
      {/* Top header */}
      {!focusMode && (
        <header
          className="sticky top-0 z-30 border-b border-cn-border bg-cn-bg/95 backdrop-blur"
          data-testid="demo-header"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/demo/preview" className="flex items-center gap-2" data-testid="demo-home-link">
                <CaraLoomLogo showWordmark={false} />
                <span className="text-sm font-semibold text-cn-text">
                  {personalizedOrg ? `CaraLoom for ${personalizedOrg}` : 'CaraLoom'}
                </span>
              </Link>
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-bold tracking-wider uppercase border border-amber-500/30"
                data-testid="demo-env-pill"
              >
                <Sparkles className="w-3 h-3" /> {DEMO_META.environment_label}
              </span>
            </div>

            <nav className="hidden md:flex items-center gap-1" aria-label="Demo primary">
              {NAV_ITEMS.map((it) => (
                <Link
                  key={it.key}
                  to={it.to}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    isActive(it.to)
                      ? 'bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent'
                      : 'text-cn-text-muted hover:text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5'
                  }`}
                  data-testid={`demo-nav-${it.key}`}
                >
                  <it.icon className="w-4 h-4" /> {it.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              {access === 'authenticated' && (
                <button
                  type="button"
                  onClick={() => setPresenterMode((v) => !v)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                    presenterMode
                      ? 'bg-cn-primary text-white'
                      : 'bg-cn-bone-100 dark:bg-white/5 text-cn-text-muted hover:text-cn-text'
                  }`}
                  data-testid="demo-presenter-toggle"
                  aria-pressed={presenterMode}
                >
                  {presenterMode ? 'Presenter Mode: ON' : 'Presenter Mode'}
                </button>
              )}
              <Link
                to="/"
                className="text-xs font-medium text-cn-text-muted hover:text-cn-text px-2 py-1.5"
                data-testid="demo-exit-link"
              >
                Exit demo
              </Link>
            </div>
          </div>

          {/* Hospital name banner */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-3">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="cn-display text-2xl md:text-3xl text-cn-text leading-tight">
                {hospital.name}
              </h1>
              <span className="text-sm text-cn-text-muted">Care Transitions · {hospital.location}</span>

              {/* Iter 179 · Presenter-only archetype switcher for Eleanor's
                  story. Same page, no route change — mid-meeting audience
                  tune. Hidden entirely from prospect-preview visitors. */}
              {presenterMode && access === 'authenticated' && (
                <div className="ml-4 inline-flex items-center gap-2" data-testid="demo-archetype-switch">
                  <label htmlFor="demo-archetype-select" className="text-[10px] uppercase tracking-widest text-cn-text-muted font-mono">
                    Story
                  </label>
                  <select
                    id="demo-archetype-select"
                    value={archetype}
                    onChange={(e) => setArchetype(e.target.value)}
                    className="text-xs bg-cn-bg dark:bg-white/[0.05] border border-cn-primary/50 dark:border-cn-accent/50 rounded-full px-3 py-1 font-semibold text-cn-primary dark:text-cn-accent"
                    data-testid="demo-archetype-select"
                  >
                    {Object.values(ELEANOR_ARCHETYPES).map((a) => (
                      <option key={a.key} value={a.key} data-testid={`demo-archetype-option-${a.key}`}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <span className="text-[10px] uppercase tracking-widest text-cn-text-muted italic ml-auto">
                Demo data
              </span>
            </div>
          </div>
        </header>
      )}

      {/* Mobile nav (chip bar) */}
      {!focusMode && (
        <nav
          className="md:hidden overflow-x-auto border-b border-cn-border bg-cn-bg/95 backdrop-blur sticky top-14 z-20"
          aria-label="Demo mobile"
        >
          <div className="flex gap-2 px-4 py-2">
            {NAV_ITEMS.map((it) => (
              <Link
                key={it.key}
                to={it.to}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  isActive(it.to)
                    ? 'bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent'
                    : 'text-cn-text-muted bg-cn-bone-100 dark:bg-white/5'
                }`}
                data-testid={`demo-nav-mobile-${it.key}`}
              >
                <it.icon className="w-3.5 h-3.5" /> {it.label}
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Focus-mode strip (minimal) */}
      {focusMode && (
        <div className="bg-cn-primary text-white px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {DEMO_META.watermark} · {hospital.name}
          </span>
          <button
            type="button"
            onClick={() => setFocusMode(false)}
            className="text-[10px] uppercase tracking-widest opacity-80 hover:opacity-100"
            data-testid="demo-exit-focus"
          >
            Exit Focus
          </button>
        </div>
      )}

      {/* Body */}
      <main
        className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10"
        data-testid="demo-body"
        id="demo-main"
      >
        <Outlet />
      </main>

      {/* Persistent watermark */}
      <div
        className="pointer-events-none fixed bottom-3 right-3 z-40 text-[10px] uppercase tracking-widest font-mono text-cn-text-muted/70 select-none"
        aria-hidden
        data-testid="demo-watermark"
      >
        {DEMO_META.watermark}
      </div>

      {/* Presenter Bar */}
      {presenterMode && access === 'authenticated' && (
        <PresenterBar
          reset={reset}
          focusMode={focusMode}
          setFocusMode={setFocusMode}
          startEleanorDemo={() => navigate('/demo/preview/patient/tx-1')}
          openDeck={() => navigate('/demo/preview/deck')}
          stops={state?.presenter_stops || []}
        />
      )}
    </div>
  );
}

function PresenterBar({ reset, focusMode, setFocusMode, startEleanorDemo, openDeck, stops }) {
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]"
      data-testid="demo-presenter-bar"
    >
      <div className="rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 shadow-2xl px-3 py-2 flex items-center gap-2 backdrop-blur">
        <button
          type="button"
          onClick={startEleanorDemo}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cn-primary text-white text-xs font-bold hover:opacity-95"
          data-testid="demo-presenter-start-eleanor"
        >
          <Play className="w-3.5 h-3.5" /> Start Eleanor Demo
        </button>

        <div className="hidden lg:flex items-center gap-1 border-l border-cn-border pl-2 ml-1">
          {stops.map((s) => (
            <Link
              key={s.key}
              to={s.to}
              className="text-xs text-cn-text-muted hover:text-cn-text px-2 py-1 rounded whitespace-nowrap"
              data-testid={`demo-presenter-stop-${s.key}`}
            >
              {s.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-1 border-l border-cn-border pl-2 ml-1">
          <button
            type="button"
            onClick={openDeck}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-cn-text-muted hover:text-cn-text"
            title="Open printable slide deck"
            data-testid="demo-presenter-download-deck"
          >
            <Printer className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Deck</span>
          </button>
          <button
            type="button"
            onClick={() => setFocusMode(!focusMode)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-cn-text-muted hover:text-cn-text"
            aria-pressed={focusMode}
            title={focusMode ? 'Exit focus mode' : 'Presentation focus mode'}
            data-testid="demo-presenter-focus"
          >
            <Focus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Focus</span>
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-cn-text-muted hover:text-cn-text"
            title="Reset demo data"
            data-testid="demo-presenter-reset"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Small shared bits reused by every demo screen ── */
export function DemoSectionTitle({ eyebrow, title, sub, right }) {
  return (
    <div className="mb-6 md:mb-8 flex items-end justify-between gap-4 flex-wrap">
      <div>
        {eyebrow && (
          <div className="text-[10px] uppercase tracking-widest text-cn-text-muted mb-2 font-mono">
            {eyebrow}
          </div>
        )}
        <h2 className="cn-display text-xl md:text-2xl text-cn-text leading-tight">{title}</h2>
        {sub && <p className="mt-1 text-sm text-cn-text-muted max-w-2xl">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export function StatCard({ value, label, tone = 'default', icon: Icon, testId }) {
  const toneCls = {
    default: 'text-cn-text',
    good:    'text-emerald-600 dark:text-emerald-300',
    warn:    'text-amber-600 dark:text-amber-300',
    danger:  'text-rose-600 dark:text-rose-300',
  }[tone] || 'text-cn-text';
  return (
    <div
      className="rounded-2xl border border-cn-border bg-cn-bg dark:bg-white/[0.02] p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-cn-text-muted mb-2">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`cn-display text-3xl md:text-4xl tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

export { Users };  // convenient re-export for demo pages that use lucide sparingly
